import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { brokerageAgreementAPI, propertyAPI, contractAPI } from '../../services/api';
import SuccessModal from '../Common/SuccessModal';
import ErrorModal from '../Common/ErrorModal';
import ConfirmModal from '../Common/ConfirmModal';
import './BrokerageAgreementCreate.css';

const BrokerageAgreementCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = id && parseInt(id) > 0;

  // Tab management
  const [activeTab, setActiveTab] = useState(1);
  const [tab1Saved, setTab1Saved] = useState(false);
  const [tab2Saved, setTab2Saved] = useState(false);
  const [tab3Saved, setTab3Saved] = useState(false);
  const [tab4Saved, setTab4Saved] = useState(false);
  const [tab5Saved, setTab5Saved] = useState(false);

  // Brokerage Agreement data
  const [brokerageAgreementId, setBrokerageAgreementId] = useState(null);
  const [dateFrom, setDateFrom] = useState(new Date().toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [propertyId, setPropertyId] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);

  // Tab 2: Units
  const [units, setUnits] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);

  // Tab 3: Parties
  const [lessor, setLessor] = useState(null);
  const [organizationId, setOrganizationId] = useState('');
  const [propertyOwnerships, setPropertyOwnerships] = useState([]);
  const [organizations, setOrganizations] = useState([]);

  // Tab 4: Agreement Scope
  const [selectedScopes, setSelectedScopes] = useState([]); // ["RentCollection", "UnitsLeasing", "UnitsMarketing"]

  // Tab 5: Additional Terms
  const [defaultTermsAr, setDefaultTermsAr] = useState('');
  const [defaultTermsEng, setDefaultTermsEng] = useState('');
  const [additionalTermsAr, setAdditionalTermsAr] = useState('');
  const [additionalTermsEng, setAdditionalTermsEng] = useState('');

  // Dropdowns
  const [dropdowns, setDropdowns] = useState({
    nationalities: [],
    identities: [],
    countries: [],
    cities: [],
    areas: [],
    districts: [],
    banks: [],
    unitFacilities: []
  });

  // Modals
  const [showAddLessorModal, setShowAddLessorModal] = useState(false);
  const [lessorFormSubmitted, setLessorFormSubmitted] = useState(false);
  const [lessorFormErrors, setLessorFormErrors] = useState({});
  const [editingOwnershipId, setEditingOwnershipId] = useState(null);
  const [deletedOwnershipIds, setDeletedOwnershipIds] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null);

  // Lessor form data
  const [lessorFormData, setLessorFormData] = useState({
    agentNumber: '',
    nameAr: '',
    nameEng: '',
    dob: '',
    nationalityId: '',
    identityTypeId: '',
    identityNumber: '',
    mobileCountryCode: '',
    mobileNumber: '',
    emailId: '',
    bankId: '',
    bankAccountNumber: '',
    areaId: '',
    cityId: '',
    districtId: '',
    streetName: '',
    buildingNumber: '',
    additionalNumber: '',
    postalCode: ''
  });


  // Supporting data
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Load dropdowns on mount
  useEffect(() => {
    loadDropdowns();
    loadOrganizations();
    loadDefaultTerms();
  }, []);

  // Check for selected property from PropertySelect page
  useEffect(() => {
    const selectedPropertyIdFromStorage = sessionStorage.getItem('selectedPropertyId');
    if (selectedPropertyIdFromStorage) {
      const propertyId = parseInt(selectedPropertyIdFromStorage);
      if (!isNaN(propertyId) && propertyId > 0) {
        handleSelectPropertyById(propertyId);
      }
      sessionStorage.removeItem('selectedPropertyId');
    }
  }, []);

  // Load agreement data if editing
  useEffect(() => {
    if (isEdit && id) {
      loadBrokerageAgreement();
    }
  }, [isEdit, id]);

  const loadDropdowns = async () => {
    try {
      const response = await propertyAPI.getDropdowns();
      const data = response.data || {};
      setDropdowns({
        nationalities: data.Nationalities || data.nationalities || [],
        identities: data.IdentityTypes || data.identityTypes || data.Identities || data.identities || [],
        countries: data.Countries || data.countries || [],
        cities: data.Cities || data.cities || [],
        areas: data.Areas || data.areas || [],
        districts: data.Districts || data.districts || [],
        banks: data.Banks || data.banks || [],
        unitFacilities: data.UnitFacilitiesAndAmenities || data.unitFacilitiesAndAmenities || []
      });
    } catch (err) {
      console.error('Failed to load dropdowns:', err);
    }
  };

  const loadOrganizations = async () => {
    try {
      const response = await brokerageAgreementAPI.getOrganizations();
      setOrganizations(response.data || []);
    } catch (err) {
      console.error('Failed to load organizations:', err);
    }
  };

  const loadDefaultTerms = async () => {
    try {
      const response = await brokerageAgreementAPI.getDefaultTerms();
      const data = response.data || {};
      setDefaultTermsAr(data.defaultTermsAr || '');
      setDefaultTermsEng(data.defaultTermsEng || '');
    } catch (err) {
      console.error('Failed to load default terms:', err);
    }
  };

  const loadBrokerageAgreement = async () => {
    try {
      setLoading(true);
      const response = await brokerageAgreementAPI.getById(id);
      const agreement = response.data || {};

      // Tab 1 data
      setBrokerageAgreementId(agreement.id || agreement.Id);
      setDateFrom(agreement.dateFrom ? new Date(agreement.dateFrom).toISOString().split('T')[0] : '');
      setDateTo(agreement.dateTo ? new Date(agreement.dateTo).toISOString().split('T')[0] : '');
      setPropertyId(agreement.propertyId || agreement.PropertyId || null);

      // Load property details
      if (agreement.propertyId || agreement.PropertyId) {
        await loadSelectedProperty(agreement.propertyId || agreement.PropertyId);
      }

      // Tab 2: Units
      if (agreement.brokerageAgreementUnits || agreement.BrokerageAgreementUnits) {
        const agreementUnits = agreement.brokerageAgreementUnits || agreement.BrokerageAgreementUnits;
        setSelectedUnits(agreementUnits.map(au => {
          const propertyUnitId = au.propertyUnitId || au.PropertyUnitId;
          const parsedId = typeof propertyUnitId === 'number' ? propertyUnitId : parseInt(propertyUnitId);
          if (isNaN(parsedId)) {
            console.warn('Invalid propertyUnitId when loading:', propertyUnitId);
            return null;
          }
          return {
            propertyUnitId: parsedId,
          agreementStatus: au.agreementStatus !== false && au.agreementStatus !== 'false',
            facilities: (au.brokerageAgreementUnitFacilities || au.BrokerageAgreementUnitFacilities || []).map(f => {
              const facilityId = f.facilityId || f.FacilityId;
              const parsedFacilityId = typeof facilityId === 'number' ? facilityId : parseInt(facilityId);
              const quantity = f.quantity || f.Quantity || 1;
              const parsedQuantity = typeof quantity === 'number' ? quantity : parseInt(quantity);
              if (isNaN(parsedFacilityId)) {
                console.warn('Invalid facilityId when loading:', facilityId);
                return null;
              }
              return {
                FacilityId: parsedFacilityId,
                Quantity: isNaN(parsedQuantity) ? 1 : parsedQuantity
              };
            }).filter(f => f !== null)
          };
        }).filter(au => au !== null));
        setTab2Saved(true);
      }

      // Tab 3: Parties
      if (agreement.brokerageAgreementLessor || agreement.BrokerageAgreementLessor) {
        const lessorData = agreement.brokerageAgreementLessor || agreement.BrokerageAgreementLessor;
        setLessor({ propertyOwnershipId: lessorData.propertyOwnershipId || lessorData.PropertyOwnershipId });
      }
      if (agreement.brokerageAgreementOrganization || agreement.BrokerageAgreementOrganization) {
        const orgData = agreement.brokerageAgreementOrganization || agreement.BrokerageAgreementOrganization;
        setOrganizationId(String(orgData.organizationId || orgData.OrganizationId || ''));
      }

      // Tab 4: Scopes
      if (agreement.brokerageAgreementScopes || agreement.BrokerageAgreementScopes) {
        const scopes = agreement.brokerageAgreementScopes || agreement.BrokerageAgreementScopes;
        setSelectedScopes(scopes.map(s => s.scopeType || s.ScopeType || '').filter(s => s));
      }

      // Tab 5: Terms
      if (agreement.brokerageAgreementTerms || agreement.BrokerageAgreementTerms) {
        const terms = agreement.brokerageAgreementTerms || agreement.BrokerageAgreementTerms;
        setAdditionalTermsAr(terms.additionalTermsAr || terms.AdditionalTermsAr || '');
        setAdditionalTermsEng(terms.additionalTermsEng || terms.AdditionalTermsEng || '');
      }

      setTab1Saved(true);
      if (agreement.brokerageAgreementUnits || agreement.BrokerageAgreementUnits) setTab2Saved(true);
      if ((agreement.brokerageAgreementLessor || agreement.BrokerageAgreementLessor) && 
          (agreement.brokerageAgreementOrganization || agreement.BrokerageAgreementOrganization)) {
        setTab3Saved(true);
      }
    } catch (err) {
      console.error('Failed to load brokerage agreement:', err);
      setErrorMessage(t('brokerageAgreement.create.messages.loadError'));
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const loadSelectedProperty = async (propId) => {
    try {
      const response = await brokerageAgreementAPI.getPropertiesWithDetails({ propertyId: parseInt(propId) });
      const data = response.data || {};
      let properties = Array.isArray(data) ? data : (data.properties || data.items || []);
      
      if (properties.length > 0) {
        setSelectedProperty(properties[0]);
        setPropertyId(parseInt(propId));
        await loadPropertyUnits(propId);
        await loadPropertyOwnerships(propId);
      }
    } catch (err) {
      console.error('Failed to load property:', err);
    }
  };

  const loadPropertyUnits = async (propId) => {
    try {
      const response = await brokerageAgreementAPI.getPropertyUnits(propId);
      setUnits(response.data || []);
    } catch (err) {
      console.error('Failed to load property units:', err);
    }
  };

  const loadPropertyOwnerships = async (propId) => {
    try {
      const response = await brokerageAgreementAPI.getPropertyOwnerships(propId);
      setPropertyOwnerships(response.data || []);
    } catch (err) {
      console.error('Failed to load property ownerships:', err);
    }
  };

  const savePropertyOwnerships = async (ownershipsToSave = null, deletedIds = null) => {
    if (!propertyId) return;
    
    try {
      const ownershipsArray = ownershipsToSave !== null ? ownershipsToSave : propertyOwnerships;
      const deletedIdsArray = deletedIds !== null ? deletedIds : deletedOwnershipIds;
      
      const parseId = (value) => {
        if (!value) return null;
        if (Array.isArray(value)) {
          const parsed = parseInt(value[0]);
          return isNaN(parsed) ? null : parsed;
        }
        if (typeof value === 'string') {
          const parsed = parseInt(value);
          return isNaN(parsed) ? null : parsed;
        }
        if (typeof value === 'number') {
          return isNaN(value) ? null : value;
        }
        return null;
      };

      const formattedOwnerships = ownershipsArray.map(ownership => {
        let ownerIdValue = null;
        if (ownership.ownerId) {
          if (Array.isArray(ownership.ownerId)) {
            const firstVal = ownership.ownerId[0];
            if (firstVal && firstVal.toString().trim() !== '') {
              const parsed = parseInt(firstVal.toString());
              ownerIdValue = isNaN(parsed) ? null : parsed;
            }
          } else if (typeof ownership.ownerId === 'string') {
            const trimmed = ownership.ownerId.trim();
            if (trimmed !== '') {
              const parsed = parseInt(trimmed);
              ownerIdValue = isNaN(parsed) ? null : parsed;
            }
          } else if (typeof ownership.ownerId === 'number') {
            ownerIdValue = isNaN(ownership.ownerId) ? null : ownership.ownerId;
          }
        }
        
        const formatted = {};
        const oId = ownership.id || ownership.Id;
        if (oId) formatted.id = oId;
        formatted.propertyId = propertyId;
        formatted.ownershipNameAr = ownership.ownershipNameAr || ownership.OwnershipNameAr || '';
        formatted.ownershipNameEng = ownership.ownershipNameEng || ownership.OwnershipNameEng || '';
        if (ownership.dob || ownership.Dob) formatted.dob = ownership.dob || ownership.Dob;
        if (parseId(ownership.nationalityId || ownership.NationalityId) !== null) formatted.nationalityId = parseId(ownership.nationalityId || ownership.NationalityId);
        if (parseId(ownership.identityTypeId || ownership.IdentityTypeId) !== null) formatted.identityTypeId = parseId(ownership.identityTypeId || ownership.IdentityTypeId);
        if (ownership.identityNumber || ownership.IdentityNumber) formatted.identityNumber = ownership.identityNumber || ownership.IdentityNumber;
        if (ownership.mobileNumberCountryCode || ownership.MobileNumberCountryCode) formatted.mobileNumberCountryCode = ownership.mobileNumberCountryCode || ownership.MobileNumberCountryCode;
        if (ownership.mobileNumber || ownership.MobileNumber) formatted.mobileNumber = ownership.mobileNumber || ownership.MobileNumber;
        if (ownership.emailId || ownership.EmailId) formatted.emailId = ownership.emailId || ownership.EmailId;
        if (parseId(ownership.bankId || ownership.BankId) !== null) formatted.bankId = parseId(ownership.bankId || ownership.BankId);
        if (ownership.bankAccountNumber || ownership.BankAccountNumber) formatted.bankAccountNumber = ownership.bankAccountNumber || ownership.BankAccountNumber;
        if (parseId(ownership.areaId || ownership.AreaId) !== null) formatted.areaId = parseId(ownership.areaId || ownership.AreaId);
        if (parseId(ownership.cityId || ownership.CityId) !== null) formatted.cityId = parseId(ownership.cityId || ownership.CityId);
        if (parseId(ownership.districtId || ownership.DistrictId) !== null) formatted.districtId = parseId(ownership.districtId || ownership.DistrictId);
        if (ownership.streetName || ownership.StreetName) formatted.streetName = ownership.streetName || ownership.StreetName;
        if (ownership.buildingNumber || ownership.BuildingNumber) formatted.buildingNumber = ownership.buildingNumber || ownership.BuildingNumber;
        if (ownership.additionalNumber || ownership.AdditionalNumber) formatted.additionalNumber = ownership.additionalNumber || ownership.AdditionalNumber;
        if (ownership.postalCode || ownership.PostalCode) formatted.postalCode = ownership.postalCode || ownership.PostalCode;
        formatted.isAgent = (ownership.isAgent || ownership.IsAgent) === true;
        if (ownership.agentNumber || ownership.AgentNumber) formatted.agentNumber = ownership.agentNumber || ownership.AgentNumber;
        if (ownerIdValue !== null) formatted.ownerId = ownerIdValue;
        
        return formatted;
      });
      
      await propertyAPI.saveTab2(propertyId, { 
        ownerships: formattedOwnerships,
        deletedOwnershipIds: deletedIdsArray
      });
      if (deletedIds === null) {
        setDeletedOwnershipIds([]);
      }
      // Reload ownerships to get updated data
      await loadPropertyOwnerships(propertyId);
    } catch (err) {
      console.error('Failed to save property ownerships:', err);
      throw err;
    }
  };

  const handleSelectPropertyById = async (propId) => {
    await loadSelectedProperty(propId);
  };

  // Helper functions to filter location dropdowns hierarchically
  const getFilteredCities = (areaId) => {
    if (!areaId) return [];
    const cities = dropdowns.cities || [];
    return cities.filter(city => {
      const cityAreaId = city.areaId || city.AreaId;
      return cityAreaId === parseInt(areaId);
    });
  };

  const getFilteredDistricts = (cityId) => {
    if (!cityId) return [];
    const districts = dropdowns.districts || [];
    return districts.filter(district => {
      const districtCityId = district.cityId || district.CityId;
      return districtCityId === parseInt(cityId);
    });
  };

  // Handle area change for lessor form - reset city and district
  const handleLessorAreaChange = (areaId) => {
    setLessorFormData({
      ...lessorFormData,
      areaId: areaId,
      cityId: '', // Reset city when area changes
      districtId: '' // Reset district when area changes
    });
    if (lessorFormErrors.areaId) {
      setLessorFormErrors({ ...lessorFormErrors, areaId: '', cityId: '', districtId: '' });
    }
  };

  // Handle city change for lessor form - reset district
  const handleLessorCityChange = (cityId) => {
    setLessorFormData({
      ...lessorFormData,
      cityId: cityId,
      districtId: '' // Reset district when city changes
    });
    if (lessorFormErrors.cityId) {
      setLessorFormErrors({ ...lessorFormErrors, cityId: '', districtId: '' });
    }
  };

  // Tab navigation validation
  const canAccessTab = (tabNumber) => {
    if (tabNumber === 1) return true;
    if (tabNumber === 2) return tab1Saved;
    if (tabNumber === 3) return tab2Saved;
    if (tabNumber === 4) return tab3Saved;
    if (tabNumber === 5) return tab4Saved;
    return false;
  };

  const handleTabClick = (tabNumber) => {
    if (canAccessTab(tabNumber)) {
      setActiveTab(tabNumber);
    }
  };

  // Tab 1: Save Brokerage Agreement
  const handleSaveTab1 = async (showSuccess = true) => {
    if (!dateFrom || !dateTo) {
      setErrorMessage(t('brokerageAgreement.create.messages.validation.dateFromRequired'));
      setShowErrorModal(true);
      return false;
    }
    
    if (!propertyId) {
      setErrorMessage(t('brokerageAgreement.create.messages.validation.propertyRequired'));
      setShowErrorModal(true);
      return false;
    }
    
    if (new Date(dateTo) < new Date(dateFrom)) {
      setErrorMessage(t('brokerageAgreement.create.messages.validation.dateToAfterDateFrom'));
      setShowErrorModal(true);
      return false;
    }

    try {
      setLoading(true);
      const data = {
        id: brokerageAgreementId || 0,
        dateFrom,
        dateTo,
        propertyId
      };
      
      const response = await brokerageAgreementAPI.saveBrokerageAgreement(data);
      
      if (response.data.success) {
        setBrokerageAgreementId(response.data.brokerageAgreementId);
        setTab1Saved(true);
        if (showSuccess) {
        setSuccessMessage(t('brokerageAgreement.create.messages.saveTab1Success'));
        setShowSuccessModal(true);
        }
        
        await loadPropertyUnits(propertyId);
        await loadPropertyOwnerships(propertyId);
        return true;
      } else {
        setErrorMessage(response.data.message || t('brokerageAgreement.create.messages.saveTab1Error'));
        setShowErrorModal(true);
        return false;
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || t('brokerageAgreement.create.messages.saveTab1Error'));
      setShowErrorModal(true);
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Tab 2: Save Units
  const handleSaveTab2 = async (showSuccess = true) => {
    if (!brokerageAgreementId) {
      setErrorMessage(t('brokerageAgreement.create.messages.validation.saveTab1First'));
      setShowErrorModal(true);
      return false;
    }

    if (selectedUnits.length === 0) {
      setErrorMessage(t('brokerageAgreement.create.messages.validation.selectAtLeastOneUnit'));
      setShowErrorModal(true);
      return false;
    }

    try {
      setLoading(true);
      
      // Helper function to safely parse ID
      const parseId = (value) => {
        if (!value) return null;
        if (Array.isArray(value)) {
          const parsed = parseInt(value[0]);
          return isNaN(parsed) ? null : parsed;
        }
        if (typeof value === 'string') {
          const parsed = parseInt(value);
          return isNaN(parsed) ? null : parsed;
        }
        if (typeof value === 'number') {
          return isNaN(value) ? null : value;
        }
        return null;
      };
      
      const data = {
        brokerageAgreementId: parseInt(brokerageAgreementId),
        brokerageAgreementUnits: selectedUnits
          .map(unit => {
            const propertyUnitId = parseId(unit.propertyUnitId);
            if (!propertyUnitId) {
              console.warn('Invalid propertyUnitId:', unit.propertyUnitId);
              return null;
            }
            return {
              propertyUnitId: propertyUnitId,
          agreementStatus: unit.agreementStatus === true || unit.agreementStatus === 'true',
              facilities: (unit.facilities || []).map(f => {
                const facilityId = parseId(f.FacilityId || f.facilityId);
                const quantity = parseId(f.Quantity || f.quantity || 1);
                if (!facilityId) {
                  console.warn('Invalid facilityId:', f);
                  return null;
                }
                return {
                  FacilityId: facilityId,
                  Quantity: quantity || 1
                };
              }).filter(f => f !== null)
            };
          })
          .filter(unit => unit !== null)
      };
      
      const response = await brokerageAgreementAPI.saveBrokerageAgreementUnits(data);
      
      if (response.data.success) {
        setTab2Saved(true);
        if (showSuccess) {
        setSuccessMessage(t('brokerageAgreement.create.messages.saveTab2Success'));
        setShowSuccessModal(true);
        }
        return true;
      } else {
        setErrorMessage(response.data.message || t('brokerageAgreement.create.messages.saveTab2Error'));
        setShowErrorModal(true);
        return false;
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || t('brokerageAgreement.create.messages.saveTab2Error'));
      setShowErrorModal(true);
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Tab 3: Save Parties
  const handleSaveTab3 = async (showSuccess = true) => {
    if (!brokerageAgreementId) {
      setErrorMessage(t('brokerageAgreement.create.messages.validation.saveTab1First'));
      setShowErrorModal(true);
      return false;
    }

    if (!lessor) {
      setErrorMessage(t('brokerageAgreement.create.messages.validation.selectLessor'));
      setShowErrorModal(true);
      return false;
    }

    if (!organizationId) {
      setErrorMessage(t('brokerageAgreement.create.messages.validation.selectOrganization'));
      setShowErrorModal(true);
      return false;
    }

    try {
      setLoading(true);
      const data = {
        brokerageAgreementId: parseInt(brokerageAgreementId),
        contractLessor: lessor ? { propertyOwnershipId: parseInt(lessor.propertyOwnershipId) } : null,
        organizationId: parseInt(organizationId)
      };
      
      const response = await brokerageAgreementAPI.saveBrokerageAgreementParties(data);
      
      if (response.data.success) {
        setTab3Saved(true);
        if (showSuccess) {
        setSuccessMessage(t('brokerageAgreement.create.messages.saveTab3Success'));
        setShowSuccessModal(true);
        }
        return true;
      } else {
        setErrorMessage(response.data.message || t('brokerageAgreement.create.messages.saveTab3Error'));
        setShowErrorModal(true);
        return false;
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || t('brokerageAgreement.create.messages.saveTab3Error'));
      setShowErrorModal(true);
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Tab 4: Save Agreement Scope
  const handleSaveTab4 = async (showSuccess = true) => {
    if (!brokerageAgreementId) {
      setErrorMessage(t('brokerageAgreement.create.messages.validation.saveTab1First'));
      setShowErrorModal(true);
      return false;
    }

    if (selectedScopes.length === 0) {
      setErrorMessage(t('brokerageAgreement.create.messages.validation.selectAtLeastOneScope'));
      setShowErrorModal(true);
      return false;
    }

    try {
      setLoading(true);
      const data = {
        brokerageAgreementId: parseInt(brokerageAgreementId),
        scopeTypes: selectedScopes // ["RentCollection", "UnitsLeasing", "UnitsMarketing"]
      };
      
      const response = await brokerageAgreementAPI.saveBrokerageAgreementScope(data);
      
      if (response.data.success) {
        setTab4Saved(true);
        if (showSuccess) {
        setSuccessMessage(t('brokerageAgreement.create.messages.saveTab4Success'));
        setShowSuccessModal(true);
        }
        return true;
      } else {
        setErrorMessage(response.data.message || t('brokerageAgreement.create.messages.saveTab4Error'));
        setShowErrorModal(true);
        return false;
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || t('brokerageAgreement.create.messages.saveTab4Error'));
      setShowErrorModal(true);
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Tab 5: Save Additional Terms
  const handleSaveTab5 = async (showSuccess = true) => {
    if (!brokerageAgreementId) {
      setErrorMessage(t('brokerageAgreement.create.messages.validation.saveTab1First'));
      setShowErrorModal(true);
      return;
    }

    try {
      setLoading(true);
      const data = {
        brokerageAgreementId: parseInt(brokerageAgreementId),
        defaultTermsAr,
        defaultTermsEng,
        additionalTermsAr,
        additionalTermsEng
      };
      
      const response = await brokerageAgreementAPI.saveBrokerageAgreementTerms(data);
      
      if (response.data.success) {
        setTab5Saved(true);
        if (showSuccess) {
        setSuccessMessage(t('brokerageAgreement.create.messages.saveTab5Success'));
        setShowSuccessModal(true);
        }
      } else {
        setErrorMessage(response.data.message || t('brokerageAgreement.create.messages.saveTab5Error'));
        setShowErrorModal(true);
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || t('brokerageAgreement.create.messages.saveTab5Error'));
      setShowErrorModal(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle Next Tab
  const handleNextTab = async () => {
    if (activeTab === 1) {
      if (!tab1Saved) {
        const success = await handleSaveTab1(false);
        if (success) {
          setActiveTab(2);
        }
      } else {
        setActiveTab(2);
      }
    } else if (activeTab === 2) {
      if (!tab2Saved) {
        const success = await handleSaveTab2(false);
        if (success) {
          setActiveTab(3);
        }
      } else {
        setActiveTab(3);
      }
    } else if (activeTab === 3) {
      if (!tab3Saved) {
        const success = await handleSaveTab3(false);
        if (success) {
          setActiveTab(4);
        }
      } else {
        setActiveTab(4);
      }
    } else if (activeTab === 4) {
      if (!tab4Saved) {
        const success = await handleSaveTab4(false);
        if (success) {
          setActiveTab(5);
        }
      } else {
        setActiveTab(5);
      }
    }
  };

  // Handle Previous Tab
  const handlePreviousTab = () => {
    if (activeTab > 1) {
      setActiveTab(activeTab - 1);
    }
  };

  // Handle Property Selection
  const handleSelectProperty = () => {
    navigate('/property/select', { state: { returnUrl: '/brokerage-agreement/create', brokerageAgreementId } });
  };

  // Handle Scope Toggle
  const handleScopeToggle = (scopeType) => {
    if (selectedScopes.includes(scopeType)) {
      setSelectedScopes(selectedScopes.filter(s => s !== scopeType));
    } else {
      setSelectedScopes([...selectedScopes, scopeType]);
    }
  };

  // Create Property Ownership (for Add New Lessor)
  const createPropertyOwnership = async (ownershipData) => {
    try {
      // Use contractAPI since both Contract and BrokerageAgreement share the same endpoint
      const response = await contractAPI.createPropertyOwnership(ownershipData);
      return response.data;
    } catch (err) {
      throw err;
    }
  };

  const scopeOptions = [
    { value: 'RentCollection', label: t('brokerageAgreement.create.fields.rentCollection') },
    { value: 'UnitsLeasing', label: t('brokerageAgreement.create.fields.unitsLeasing') },
    { value: 'UnitsMarketing', label: t('brokerageAgreement.create.fields.unitsMarketing') }
  ];

  return (
    <div className="page-container">
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 className="page-title">
          {isEdit ? t('brokerageAgreement.create.editTitle') : t('brokerageAgreement.create.title')}
        </h2>
      </div>
  

      <div className="property-form-container">
        {/* Tabs Navigation */}
        <div className="property-tabs">
          <div 
            className={`tab-header ${activeTab === 1 ? 'active' : ''} ${!canAccessTab(1) ? 'disabled' : ''}`} 
            onClick={() => handleTabClick(1)}
          >
            <i className="fas fa-building"></i>
            <span>{t('brokerageAgreement.create.tabs.tab1')}</span>
            {tab1Saved && <i className="fas fa-check-circle text-success ms-2"></i>}
          </div>
          <div 
            className={`tab-header ${activeTab === 2 ? 'active' : ''} ${!canAccessTab(2) ? 'disabled' : ''}`} 
            onClick={() => handleTabClick(2)}
          >
            <i className="fas fa-door-open"></i>
            <span>{t('brokerageAgreement.create.tabs.tab2')}</span>
            {tab2Saved && <i className="fas fa-check-circle text-success ms-2"></i>}
          </div>
          <div 
            className={`tab-header ${activeTab === 3 ? 'active' : ''} ${!canAccessTab(3) ? 'disabled' : ''}`} 
            onClick={() => handleTabClick(3)}
          >
            <i className="fas fa-users"></i>
            <span>{t('brokerageAgreement.create.tabs.tab3')}</span>
            {tab3Saved && <i className="fas fa-check-circle text-success ms-2"></i>}
          </div>
          <div 
            className={`tab-header ${activeTab === 4 ? 'active' : ''} ${!canAccessTab(4) ? 'disabled' : ''}`} 
            onClick={() => handleTabClick(4)}
          >
            <i className="fas fa-list-check"></i>
            <span>{t('brokerageAgreement.create.tabs.tab4')}</span>
            {tab4Saved && <i className="fas fa-check-circle text-success ms-2"></i>}
          </div>
          <div 
            className={`tab-header ${activeTab === 5 ? 'active' : ''} ${!canAccessTab(5) ? 'disabled' : ''}`} 
            onClick={() => handleTabClick(5)}
          >
            <i className="fas fa-file-contract"></i>
            <span>{t('brokerageAgreement.create.tabs.tab5')}</span>
            {tab5Saved && <i className="fas fa-check-circle text-success ms-2"></i>}
          </div>
        </div>

        {/* Tab Content */}
        <div className="property-tab-content">
          {/* Tab 1: Select Property - Same as Contract */}
          {activeTab === 1 && (
            <div className="tab-panel">
              <h3 className="tab-title">{t('brokerageAgreement.create.fields.selectProperty')}</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-calendar"></i> {t('brokerageAgreement.create.fields.dateFrom')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-calendar input-icon"></i>
                    <input
                      type="date"
                      className="form-input"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-calendar"></i> {t('brokerageAgreement.create.fields.dateTo')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-calendar input-icon"></i>
                    <input
                      type="date"
                      className="form-input"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="card mb-4">
                <div className="card-header">
                  <h5><i className="fas fa-building text-primary"></i> <span className="text-primary">{t('brokerageAgreement.create.fields.selectProperty')}</span></h5>
                </div>
                <div className="card-body">
                  {selectedProperty ? (
                    <div className="property-display-card">
                      <div className="row align-items-center">
                        <div className="col-md-10">
                          <div className="row mb-2">
                            <div className="col-12">
                              <h5 className="mb-1" style={{ display: 'inline-block', marginRight: '0.5rem' }}>
                                {selectedProperty.nameEng || selectedProperty.NameEng}
                              </h5>
                              {(selectedProperty.nameAr || selectedProperty.NameAr) && (
                                <span className="text-muted small">
                                  - {selectedProperty.nameAr || selectedProperty.NameAr}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="row mb-2">
                            <div className="col-12 d-flex flex-wrap gap-3 align-items-center">
                              {(selectedProperty.code || selectedProperty.Code) && (
                                <span className="small">
                                  <strong>{t('brokerageAgreement.create.fields.code')}:</strong> {selectedProperty.code || selectedProperty.Code}
                                </span>
                              )}
                              {selectedProperty.realEstateType && (
                                <span className="small">
                                  <strong>{t('brokerageAgreement.create.fields.type')}:</strong> {selectedProperty.realEstateType.nameEng || selectedProperty.realEstateType.NameEng}
                                </span>
                              )}
                              {selectedProperty.city && (
                                <span className="small">
                                  <strong>{t('brokerageAgreement.create.fields.city')}:</strong> {selectedProperty.city.cityNameEng || selectedProperty.city.CityNameEng}
                                </span>
                              )}
                            </div>
                          </div>
                          {selectedProperty.propertyFacilities && Array.isArray(selectedProperty.propertyFacilities) && selectedProperty.propertyFacilities.length > 0 && (
                            <div className="row mt-3">
                              <div className="col-12">
                                <label className="form-label facilities-section-label" style={{ marginBottom: '1rem' }}>
                                  <i className="fas fa-star"></i> {t('brokerageAgreement.create.fields.facilitiesAndAmenities')}
                                </label>
                                <div className="facilities-section-container">
                                  <div className="facilities-grid">
                                    {selectedProperty.propertyFacilities.map((facility, idx) => (
                                      <div key={idx} className="facility-card active" style={{ cursor: 'default' }}>
                                        <div className="facility-card-content">
                                          <div className="facility-icon">
                                            <i className="fas fa-check-circle"></i>
                                          </div>
                                          <div className="facility-info">
                                            <div className="facility-name">{facility.nameEng || facility.NameEng || facility}</div>
                                            {facility.nameAr || facility.NameAr ? (
                                              <div className="facility-name-ar">{facility.nameAr || facility.NameAr}</div>
                                            ) : null}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="col-md-2 text-center text-md-end">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={handleSelectProperty}
                            title={t('brokerageAgreement.create.fields.changeProperty')}
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSelectProperty}
                    >
                      <i className="fas fa-plus"></i> {t('brokerageAgreement.create.fields.addProperty')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Tab 2: Select Units */}
          {activeTab === 2 && (
            <div className="tab-panel">
              <h3 className="tab-title">{t('brokerageAgreement.create.fields.selectUnits')}</h3>
              
                  {!propertyId ? (
                    <div className="alert alert-warning">
                  <i className="fas fa-exclamation-triangle"></i> {t('brokerageAgreement.create.messages.selectPropertyFirst')}
                    </div>
                  ) : units.length === 0 ? (
                    <div className="alert alert-info">
                  <i className="fas fa-info-circle"></i> {t('brokerageAgreement.create.messages.noUnitsAvailable')}
                    </div>
                  ) : (
                <div className="units-grid-modern">
                      {units.map((unit) => {
                    const unitId = unit.id || unit.Id;
                        const isSelected = selectedUnits.some(su => su.propertyUnitId === unitId);
                        const selectedUnit = selectedUnits.find(su => su.propertyUnitId === unitId);
                        return (
                          <div
                            key={unitId}
                        className={`unit-card-modern ${isSelected ? 'selected' : ''}`}
                        onClick={() => {
                          if (!isSelected) {
                            setSelectedUnits([...selectedUnits, {
                              propertyUnitId: unitId,
                              agreementStatus: true,
                              facilities: []
                            }]);
                          } else {
                            setSelectedUnits(selectedUnits.filter(su => su.propertyUnitId !== unitId));
                          }
                        }}
                          >
                        <div className="unit-card-header">
                          <div className="unit-card-checkbox">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                e.stopPropagation();
                                  if (e.target.checked) {
                                    setSelectedUnits([...selectedUnits, {
                                      propertyUnitId: unitId,
                                      agreementStatus: true,
                                      facilities: []
                                    }]);
                                  } else {
                                    setSelectedUnits(selectedUnits.filter(su => su.propertyUnitId !== unitId));
                                  }
                                }}
                              />
                          </div>
                          <div className="unit-card-title">
                            <div className="unit-card-name">
                              <span className="unit-number">{t('brokerageAgreement.create.fields.unit')} {unit.unitNumber || unit.UnitNumber || unitId}</span>
                                {(unit.unitType || unit.UnitType) && (
                                <span className="unit-type">{(unit.unitType?.nameEng || unit.UnitType?.NameEng || unit.unitType?.NameEng || unit.UnitType?.nameEng)}</span>
                                )}
                            </div>
                          </div>
                        </div>
                        <div className="unit-card-body">
                          <div className="unit-card-details-grid">
                              {(unit.unitArea || unit.UnitArea) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-ruler-combined"></i>
                                <span><strong>{t('brokerageAgreement.create.fields.area')}:</strong> {unit.unitArea || unit.UnitArea} m²</span>
                              </div>
                            )}
                            {(unit.floorNumber || unit.FloorNumber) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-layer-group"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.floor')}:</strong> {unit.floorNumber || unit.FloorNumber}</span>
                              </div>
                            )}
                            {(unit.numberOfRooms || unit.NumberOfRooms) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-door-open"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.rooms')}:</strong> {unit.numberOfRooms || unit.NumberOfRooms}</span>
                              </div>
                              )}
                              {(unit.unitUsage || unit.UnitUsage) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-tag"></i>
                                <span><strong>{t('brokerageAgreement.create.fields.usage')}:</strong> {(unit.unitUsage?.nameEng || unit.UnitUsage?.NameEng || unit.unitUsage?.NameEng || unit.UnitUsage?.nameEng)}</span>
                              </div>
                              )}
                            {(unit.establishedDate || unit.EstablishedDate) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-calendar-alt"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.establishedDate')}:</strong> {new Date(unit.establishedDate || unit.EstablishedDate).toLocaleDateString()}</span>
                            </div>
                            )}
                            {(unit.unitDirection || unit.UnitDirection) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-compass"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.direction')}:</strong> {unit.unitDirection || unit.UnitDirection}</span>
                                          </div>
                            )}
                            {(unit.unitFinishing || unit.UnitFinishing) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-paint-brush"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.finishing')}:</strong> {unit.unitFinishing || unit.UnitFinishing}</span>
                                          </div>
                            )}
                            {(unit.furnished || unit.Furnished) !== undefined && (
                              <div className="unit-detail-item">
                                <i className="fas fa-couch"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.furnished')}:</strong> {(unit.furnished || unit.Furnished) ? t('common.yes') : t('common.no')}</span>
                                        </div>
                            )}
                            {(unit.countOfParking || unit.CountOfParking) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-parking"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.parking')}:</strong> {unit.countOfParking || unit.CountOfParking}</span>
                                      </div>
                            )}
                            {(unit.unitFrontLength || unit.UnitFrontLength) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-ruler"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.frontLength')}:</strong> {unit.unitFrontLength || unit.UnitFrontLength} m</span>
                              </div>
                            )}
                            {(unit.unitLength || unit.UnitLength) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-ruler-horizontal"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.length')}:</strong> {unit.unitLength || unit.UnitLength} m</span>
                              </div>
                            )}
                            {(unit.unitWidth || unit.UnitWidth) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-ruler-vertical"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.width')}:</strong> {unit.unitWidth || unit.UnitWidth} m</span>
                              </div>
                            )}
                            {(unit.unitHeight || unit.UnitHeight) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-arrows-alt-v"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.height')}:</strong> {unit.unitHeight || unit.UnitHeight} m</span>
                              </div>
                            )}
                            {(unit.electricityMeter || unit.ElectricityMeter) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-bolt"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.electricityMeter')}:</strong> {unit.electricityMeter || unit.ElectricityMeter}</span>
                              </div>
                            )}
                            {(unit.waterMeter || unit.WaterMeter) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-tint"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.waterMeter')}:</strong> {unit.waterMeter || unit.WaterMeter}</span>
                              </div>
                            )}
                            {(unit.gasMeterNo || unit.GasMeterNo) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-fire"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.gasMeter')}:</strong> {unit.gasMeterNo || unit.GasMeterNo}</span>
                              </div>
                            )}
                            {(unit.includeMezzanine || unit.IncludeMezzanine) !== undefined && (
                              <div className="unit-detail-item">
                                <i className="fas fa-layer-group"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.mezzanine')}:</strong> {(unit.includeMezzanine || unit.IncludeMezzanine) ? t('common.yes') : t('common.no')}</span>
                              </div>
                            )}
                            {(unit.kitchenCabinets || unit.KitchenCabinets) !== undefined && (
                              <div className="unit-detail-item">
                                <i className="fas fa-utensils"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.kitchenCabinets')}:</strong> {(unit.kitchenCabinets || unit.KitchenCabinets) ? t('common.yes') : t('common.no')}</span>
                              </div>
                            )}
                          </div>
                          {unit.facilities && Array.isArray(unit.facilities) && unit.facilities.length > 0 && (
                            <div className="unit-existing-facilities">
                              <div className="facilities-badge-container">
                                {unit.facilities.slice(0, 3).map((facility, idx) => (
                                  <span key={idx} className="facility-badge">
                                    <i className="fas fa-check-circle"></i>
                                    {facility.nameEng || facility.NameEng || facility}
                                  </span>
                                ))}
                                {unit.facilities.length > 3 && (
                                  <span className="facility-badge-more">
                                    +{unit.facilities.length - 3} {t('contract.create.fields.more')}
                                  </span>
                                )}
                                  </div>
                                </div>
                          )}
                              </div>
                        {isSelected && (
                          <div className="unit-card-actions">
                            <div className="unit-facilities-selector">
                              <div className="facilities-selector-header">
                                <i className="fas fa-star"></i>
                                <span>{t('contract.create.fields.selectFacilities')}</span>
                              </div>
                              <div className="facilities-selector-grid">
                                <div className="row g-2" style={{ width: '100%', margin: 0, padding: '0.5rem' }}>
                                  {(dropdowns.unitFacilities || []).map(facility => {
                                    const facilityId = facility.id || facility.Id;
                                    const unitFacilities = selectedUnit?.facilities || [];
                                    const existingFacility = unitFacilities.find(f => (f.FacilityId || f.facilityId) === facilityId);
                                    const isSelected = existingFacility !== undefined;
                                    const quantity = existingFacility ? (existingFacility.Quantity || existingFacility.quantity || 1) : 1;
                                    return (
                                      <div key={facilityId} className="col-md-3" style={{ padding: '0.5rem', width: '100%' }}>
                                        <div
                                          className={`facility-card contract-unit-facility-card ${isSelected ? 'active' : ''}`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const updatedUnits = [...selectedUnits];
                                            const unitIndex = updatedUnits.findIndex(su => su.propertyUnitId === (unit.id || unit.Id));
                                            if (unitIndex >= 0) {
                                              const currentFacilities = updatedUnits[unitIndex].facilities || [];
                                              if (isSelected) {
                                                // Remove facility
                                                updatedUnits[unitIndex] = {
                                                  ...updatedUnits[unitIndex],
                                                  facilities: currentFacilities.filter(f => (f.FacilityId || f.facilityId) !== facilityId)
                                                };
                                              } else {
                                                // Add facility
                                                updatedUnits[unitIndex] = {
                                                  ...updatedUnits[unitIndex],
                                                  facilities: [...currentFacilities, { FacilityId: facilityId, Quantity: 1 }]
                                                };
                                              }
                                              setSelectedUnits(updatedUnits);
                                            }
                                          }}
                                        >
                                          <div className="facility-card-content">
                                            <div className="facility-icon">
                                              {facility.icon || facility.Icon ? (
                                                <img src={facility.icon || facility.Icon} alt={facility.nameEng || facility.NameEng} />
                                              ) : (
                                                <i className="fas fa-check-circle"></i>
                                              )}
                                            </div>
                                            <div className="facility-info">
                                              <div className="facility-name">{facility.nameEng || facility.NameEng}</div>
                                              {(facility.nameAr || facility.NameAr) && (
                                                <div className="facility-name-ar">{facility.nameAr || facility.NameAr}</div>
                                              )}
                                            </div>
                            {isSelected && (
                                              <div className="facility-quantity-select">
                                                <select
                                                  className="form-select-sm facility-quantity-dropdown"
                                                  value={quantity}
                                                  disabled={!isSelected}
                                                  onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                    e.stopPropagation();
                                                    const newQuantity = parseInt(e.target.value);
                                                    const updatedUnits = [...selectedUnits];
                                                    const unitIndex = updatedUnits.findIndex(su => su.propertyUnitId === (unit.id || unit.Id));
                                                    if (unitIndex >= 0) {
                                                      const currentFacilities = updatedUnits[unitIndex].facilities || [];
                                                      const facilityIndex = currentFacilities.findIndex(f => (f.FacilityId || f.facilityId) === facilityId);
                                                      const newFacilities = [...currentFacilities];
                                                      if (facilityIndex >= 0) {
                                                        newFacilities[facilityIndex] = {
                                                          FacilityId: facilityId,
                                                          Quantity: newQuantity
                                                        };
                                                      } else {
                                                        newFacilities.push({
                                                          FacilityId: facilityId,
                                                          Quantity: newQuantity
                                                        });
                                                      }
                                                      updatedUnits[unitIndex] = {
                                                        ...updatedUnits[unitIndex],
                                                        facilities: newFacilities
                                                      };
                                                      setSelectedUnits(updatedUnits);
                                                    }
                                  }}
                                >
                                                  {Array.from({ length: 20 }, (_, i) => i + 1).map(num => (
                                                    <option key={num} value={num}>{num}</option>
                                                  ))}
                                                </select>
                              </div>
                            )}
                                          </div>
                                        </div>
                          </div>
                        );
                      })}
                                </div>
                              </div>
                            </div>
                    </div>
                  )}
                </div>
                    );
                  })}
              </div>
              )}
            </div>
          )}

          {/* Tab 3: Select Parties */}
          {activeTab === 3 && (
            <div className="tab-panel">
              <h3 className="tab-title">{t('brokerageAgreement.create.tabs.tab3')}</h3>
              
              {/* Lessor Section */}
              <div className="mb-4">
                <div className="d-flex justify-content-end align-items-center mb-3">
                  {propertyId && (
                    <button
                      type="button"
                      className="btn btn-sm"
                      title={t('contract.create.fields.addLessor')}
                      style={{ padding: '0.5rem 1rem', color: '#bf4f1f', backgroundColor: 'transparent', border: '1px solid #bf4f1f' }}
                      onClick={() => {
                        setLessorFormData({
                          agentNumber: '',
                          nameAr: '',
                          nameEng: '',
                          dob: '',
                          nationalityId: '',
                          identityTypeId: '',
                          identityNumber: '',
                          mobileCountryCode: '',
                          mobileNumber: '',
                          emailId: '',
                          bankId: '',
                          bankAccountNumber: '',
                          areaId: '',
                          cityId: '',
                          districtId: '',
                          streetName: '',
                          buildingNumber: '',
                          additionalNumber: '',
                          postalCode: ''
                        });
                        setEditingOwnershipId(null);
                        setShowAddLessorModal(true);
                      }}
                    >
                      <i className="fas fa-user-plus"></i> {t('contract.create.fields.addLessor')}
                    </button>
                  )}
                </div>
                {!propertyId ? (
                  <div className="alert alert-warning">
                      <i className="fas fa-exclamation-triangle"></i> {t('contract.create.messages.pleaseSelectPropertyFirst')}
                  </div>
                ) : propertyOwnerships.length === 0 ? (
                  <div className="alert alert-info">
                      <i className="fas fa-info-circle"></i> {t('contract.create.fields.noPropertyOwnerships')}
                  </div>
                ) : (
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                            <th></th>
                            <th>{t('contract.create.tables.nameEnglish')}</th>
                            <th>{t('contract.create.tables.nameArabic')}</th>
                            <th>{t('contract.create.tables.identityNumber')}</th>
                            <th>{t('contract.create.tables.actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {propertyOwnerships.map((ownership) => {
                          const ownershipId = ownership.id || ownership.Id;
                            const isSelected = lessor?.propertyOwnershipId === ownershipId;
                          return (
                              <tr key={ownershipId}>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => {
                                      if (isSelected) {
                                        setLessor(null);
                                      } else {
                                        setLessor({ propertyOwnershipId: ownershipId });
                                      }
                                    }}
                                  />
                                </td>
                                <td>{ownership.ownershipNameEng || ownership.OwnershipNameEng || t('common.notAvailable')}</td>
                              <td>{ownership.ownershipNameAr || ownership.OwnershipNameAr || '-'}</td>
                              <td>{ownership.identityNumber || ownership.IdentityNumber || '-'}</td>
                              <td>
                                <div className="action-buttons-cell">
                                  <button
                                    type="button"
                                      className="btn btn-sm btn-edit"
                                      onClick={() => {
                                        setLessorFormData({
                                          agentNumber: ownership.agentNumber || ownership.AgentNumber || '',
                                          nameAr: ownership.ownershipNameAr || ownership.OwnershipNameAr || '',
                                          nameEng: ownership.ownershipNameEng || ownership.OwnershipNameEng || '',
                                          dob: ownership.dob || ownership.Dob || '',
                                          nationalityId: ownership.nationalityId || ownership.NationalityId || '',
                                          identityTypeId: ownership.identityTypeId || ownership.IdentityTypeId || '',
                                          identityNumber: ownership.identityNumber || ownership.IdentityNumber || '',
                                          mobileCountryCode: ownership.mobileNumberCountryCode || ownership.MobileNumberCountryCode || '',
                                          mobileNumber: ownership.mobileNumber || ownership.MobileNumber || '',
                                          emailId: ownership.emailId || ownership.EmailId || '',
                                          bankId: ownership.bankId || ownership.BankId || '',
                                          bankAccountNumber: ownership.bankAccountNumber || ownership.BankAccountNumber || '',
                                          areaId: ownership.areaId || ownership.AreaId || '',
                                          cityId: ownership.cityId || ownership.CityId || '',
                                          districtId: ownership.districtId || ownership.DistrictId || '',
                                          streetName: ownership.streetName || ownership.StreetName || '',
                                          buildingNumber: ownership.buildingNumber || ownership.BuildingNumber || '',
                                          additionalNumber: ownership.additionalNumber || ownership.AdditionalNumber || '',
                                          postalCode: ownership.postalCode || ownership.PostalCode || ''
                                        });
                                        setEditingOwnershipId(ownershipId);
                                        setShowAddLessorModal(true);
                                      }}
                                      title={t('common.edit') + ' ' + t('contract.create.fields.lessor')}
                                  >
                                      <i className="fas fa-edit"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-delete"
                                      title={t('common.delete')}
                                      onClick={() => {
                                        setConfirmAction(() => async () => {
                                          try {
                                            setLoading(true);
                                            // If this ownership is selected as lessor, clear the selection
                                            if (isSelected) {
                                              setLessor(null);
                                            }
                                            // Track deleted ownership ID if it's an existing ownership (has an ID > 0)
                                            const deletedIds = ownershipId && ownershipId > 0 ? [ownershipId] : [];
                                            // Remove from list
                                            const updatedOwnerships = propertyOwnerships.filter(o => (o.id || o.Id) !== ownershipId);
                                            setPropertyOwnerships(updatedOwnerships);
                                            // Save to backend with the updated array and deleted IDs
                                            await savePropertyOwnerships(updatedOwnerships, deletedIds);
                                            setSuccessMessage(t('contract.create.messages.lessorDeletedSuccessfully'));
                                            setShowSuccessModal(true);
                                            setShowConfirmModal(false);
                                          } catch (err) {
                                            setErrorMessage(err.response?.data?.message || t('contract.create.messages.failedToDeleteLessor'));
                                            setShowErrorModal(true);
                                            setShowConfirmModal(false);
                                          } finally {
                                            setLoading(false);
                                          }
                                        });
                                        setConfirmData({
                                          title: t('contract.create.messages.confirmDeleteLessorTitle') || t('common.confirmDelete'),
                                          message: t('contract.create.messages.confirmDeleteLessor') || t('common.confirmDeleteMessage'),
                                          confirmText: t('common.delete'),
                                          cancelText: t('common.cancel')
                                        });
                                        setShowConfirmModal(true);
                                      }}
                                    >
                                      <i className="fas fa-trash"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Second Party - Organization */}
              <div className="card mb-4">
                <div className="card-header">
                  <h6 style={{ color: '#bf4f1f', fontWeight: 600 }}>
                    <i className="fas fa-building"></i> {t('brokerageAgreement.create.fields.secondParty')}
                  </h6>
                </div>
                <div className="card-body">
                  <div className="form-group">
                    <label className="form-label">
                      <i className="fas fa-building"></i> {t('brokerageAgreement.create.fields.selectOrganization')} <span className="text-danger">*</span>
                    </label>
                    <div className="input-wrapper">
                      <i className="fas fa-building input-icon"></i>
                      <select
                        className="form-select"
                        value={organizationId}
                        onChange={(e) => setOrganizationId(e.target.value)}
                        required
                      >
                        <option value="">{t('brokerageAgreement.create.placeholders.selectOrganization')}</option>
                        {organizations.map(org => (
                          <option key={org.id || org.Id} value={org.id || org.Id}>
                            {org.organizationNameEng || org.OrganizationNameEng || org.organizationNameAr || org.OrganizationNameAr} 
                            {(org.organizationCode || org.OrganizationCode) && ` - ${org.organizationCode || org.OrganizationCode}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tab 4: Agreement Scope */}
          {activeTab === 4 && (
            <div className="tab-panel">
              <h3 className="tab-title">{t('brokerageAgreement.create.tabs.tab4')}</h3>
              <div className="card mb-4">
                <div className="card-header">
                  <h6 style={{ color: '#bf4f1f', fontWeight: 600 }}>
                    <i className="fas fa-list-check"></i> {t('brokerageAgreement.create.fields.selectAgreementScope')}
                  </h6>
                </div>
                <div className="card-body">
                  <div className="facilities-section-container">
                    <div className="row g-3" style={{ width: '100%', margin: 0 }}>
                      {scopeOptions.map(scope => {
                        const isSelected = selectedScopes.includes(scope.value);
                        return (
                          <div key={scope.value} className="col-md-4" style={{ padding: '0.5rem' }}>
                            <div
                              className={`facility-card contract-unit-facility-card ${isSelected ? 'active' : ''}`}
                              onClick={() => handleScopeToggle(scope.value)}
                              style={{ cursor: 'pointer' }}
                            >
                              <div className="facility-card-content">
                                <div className="facility-icon">
                                  <i className="fas fa-check-circle"></i>
                                </div>
                                <div className="facility-info">
                                  <div className="facility-name">{scope.label}</div>
                                </div>
                                {isSelected && (
                                  <div className="facility-quantity-select">
                                    <i className="fas fa-check text-success" style={{ fontSize: '1.2rem' }}></i>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tab 5: Additional & Terms */}
          {activeTab === 5 && (
            <div className="tab-panel">
              <h3 className="tab-title">{t('brokerageAgreement.create.tabs.tab5')}</h3>
              
              {/* Default Terms Section (Read-Only) */}
              <div className="card mb-4">
                <div className="card-header">
                  <h6 style={{ color: '#bf4f1f', fontWeight: 600 }}>
                    <i className="fas fa-file-contract"></i> {t('brokerageAgreement.create.fields.defaultTermsReadOnly')}
                  </h6>
                </div>
                <div className="card-body">
                  <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                    {/* Arabic Section */}
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-language"></i> {t('brokerageAgreement.create.fields.defaultTermsArabic')}
                      </label>
                      <textarea
                        className="form-input"
                        value={defaultTermsAr}
                        readOnly
                        style={{ 
                          minHeight: '400px', 
                          resize: 'vertical',
                          backgroundColor: '#f8f9fa',
                          cursor: 'not-allowed'
                        }}
                      />
                    </div>
                    {/* English Section */}
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-language"></i> {t('brokerageAgreement.create.fields.defaultTermsEnglish')}
                      </label>
                      <textarea
                        className="form-input"
                        value={defaultTermsEng}
                        readOnly
                        style={{ 
                          minHeight: '400px', 
                          resize: 'vertical',
                          backgroundColor: '#f8f9fa',
                          cursor: 'not-allowed'
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Terms Section (Editable) */}
              <div className="card mb-4">
                <div className="card-header">
                  <h6 style={{ color: '#bf4f1f', fontWeight: 600 }}>
                    <i className="fas fa-plus-circle"></i> {t('brokerageAgreement.create.fields.additionalTermsEditable')}
                  </h6>
                </div>
                <div className="card-body">
                  <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                    {/* Arabic Section */}
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-language"></i> {t('brokerageAgreement.create.fields.additionalTermsArabic')}
                      </label>
                      <textarea
                        className="form-input"
                        value={additionalTermsAr}
                        onChange={(e) => setAdditionalTermsAr(e.target.value)}
                        placeholder={t('brokerageAgreement.create.placeholders.enterAdditionalTermsArabic')}
                        style={{ minHeight: '300px', resize: 'vertical' }}
                      />
                    </div>
                    {/* English Section */}
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-language"></i> {t('brokerageAgreement.create.fields.additionalTermsEnglish')}
                      </label>
                      <textarea
                        className="form-input"
                        value={additionalTermsEng}
                        onChange={(e) => setAdditionalTermsEng(e.target.value)}
                        placeholder={t('brokerageAgreement.create.placeholders.enterAdditionalTermsEnglish')}
                        style={{ minHeight: '300px', resize: 'vertical' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className="btn btn-secondary tab-nav-btn"
            onClick={handlePreviousTab}
            disabled={activeTab === 1}
          >
            {t('brokerageAgreement.create.buttons.previous')}
          </button>
          <div>
            {activeTab < 5 ? (
              <button
                className="btn btn-primary tab-nav-btn"
                onClick={handleNextTab}
              >
                {t('brokerageAgreement.create.buttons.next')}
              </button>
            ) : (
              <button
                className="btn btn-success"
                onClick={async () => {
                  if (!tab5Saved) {
                    await handleSaveTab5();
                  }
                  if (tab5Saved) {
                    setSuccessMessage(t('brokerageAgreement.create.messages.createSuccess'));
                    setShowSuccessModal(true);
                    // Redirect to list after successful save (only for create, not edit)
                    if (!isEdit) {
                      setTimeout(() => {
                        navigate('/brokerage-agreement');
                      }, 2000);
                    }
                  }
                }}
              >
                <i className="fas fa-check"></i> {t('brokerageAgreement.create.buttons.finish')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Add Lessor Modal - Same as Contract */}
      {showAddLessorModal && propertyId && (
        <div className="modal-overlay" onClick={() => setShowAddLessorModal(false)}>
          <div className="modal-content modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-user-plus"></i> {editingOwnershipId ? t('common.edit') + ' ' + t('contract.create.fields.lessor') : t('contract.create.fields.addLessor')}
              </h5>
              <button type="button" className="btn-close" onClick={() => {
                setShowAddLessorModal(false);
                setLessorFormSubmitted(false);
                setLessorFormErrors({});
              }}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-form-grid-4">
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-language"></i> {t('brokerageAgreement.create.modals.addLessor.fields.nameAr')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-language input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${lessorFormSubmitted && !lessorFormData.nameAr.trim() ? 'is-invalid' : ''}`}
                      value={lessorFormData.nameAr}
                      onChange={(e) => {
                        setLessorFormData({ ...lessorFormData, nameAr: e.target.value });
                        if (lessorFormErrors.nameAr) {
                          setLessorFormErrors({ ...lessorFormErrors, nameAr: '' });
                        }
                      }}
                      placeholder={t('brokerageAgreement.create.modals.addLessor.placeholders.enterArabicName')}
                      required
                    />
                  </div>
                  {lessorFormSubmitted && !lessorFormData.nameAr.trim() && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.nameArRequired')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-font"></i> {t('brokerageAgreement.create.modals.addLessor.fields.nameEng')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-font input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${lessorFormSubmitted && !lessorFormData.nameEng.trim() ? 'is-invalid' : ''}`}
                      value={lessorFormData.nameEng}
                      onChange={(e) => {
                        setLessorFormData({ ...lessorFormData, nameEng: e.target.value });
                        if (lessorFormErrors.nameEng) {
                          setLessorFormErrors({ ...lessorFormErrors, nameEng: '' });
                        }
                      }}
                      placeholder={t('brokerageAgreement.create.modals.addLessor.placeholders.enterEnglishName')}
                      required
                    />
                  </div>
                  {lessorFormSubmitted && !lessorFormData.nameEng.trim() && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.nameEngRequired')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-calendar"></i> {t('brokerageAgreement.create.modals.addLessor.fields.dob')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-calendar input-icon"></i>
                    <input
                      type="date"
                      className={`form-input ${lessorFormSubmitted && !lessorFormData.dob ? 'is-invalid' : ''}`}
                      value={lessorFormData.dob}
                      onChange={(e) => {
                        setLessorFormData({ ...lessorFormData, dob: e.target.value });
                        if (lessorFormErrors.dob) {
                          setLessorFormErrors({ ...lessorFormErrors, dob: '' });
                        }
                      }}
                      required
                    />
                  </div>
                  {lessorFormSubmitted && !lessorFormData.dob && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.dobRequired')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-flag"></i> {t('brokerageAgreement.create.modals.addLessor.fields.nationality')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-flag input-icon"></i>
                    <select
                      className={`form-select ${lessorFormSubmitted && !lessorFormData.nationalityId ? 'is-invalid' : ''}`}
                      value={lessorFormData.nationalityId}
                      onChange={(e) => {
                        setLessorFormData({ ...lessorFormData, nationalityId: e.target.value });
                        if (lessorFormErrors.nationalityId) {
                          setLessorFormErrors({ ...lessorFormErrors, nationalityId: '' });
                        }
                      }}
                      required
                    >
                      <option value="">-- {t('contract.create.fields.select') || t('common.select') || 'Select'} --</option>
                      {(dropdowns.nationalities || []).map(nat => (
                        <option key={nat.id || nat.Id} value={nat.id || nat.Id}>
                          {nat.nationalityNameEng || nat.NationalityNameEng}
                        </option>
                      ))}
                    </select>
                  </div>
                  {lessorFormSubmitted && !lessorFormData.nationalityId && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.nationalityRequired')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-id-card"></i> {t('brokerageAgreement.create.modals.addLessor.fields.identityType')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-id-card input-icon"></i>
                    <select
                      className={`form-select ${lessorFormSubmitted && !lessorFormData.identityTypeId ? 'is-invalid' : ''}`}
                      value={lessorFormData.identityTypeId}
                      onChange={(e) => {
                        setLessorFormData({ ...lessorFormData, identityTypeId: e.target.value });
                        if (lessorFormErrors.identityTypeId) {
                          setLessorFormErrors({ ...lessorFormErrors, identityTypeId: '' });
                        }
                      }}
                      required
                    >
                      <option value="">-- {t('contract.create.fields.select') || t('common.select') || 'Select'} --</option>
                      {(dropdowns.identities || []).map(identity => (
                        <option key={identity.id || identity.Id} value={identity.id || identity.Id}>
                          {identity.identityNameEng || identity.IdentityNameEng}
                        </option>
                      ))}
                    </select>
                  </div>
                  {lessorFormSubmitted && !lessorFormData.identityTypeId && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.identityTypeRequired')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-hashtag"></i> {t('brokerageAgreement.create.modals.addLessor.fields.identityNumber')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-hashtag input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${lessorFormSubmitted && !lessorFormData.identityNumber.trim() ? 'is-invalid' : ''}`}
                      value={lessorFormData.identityNumber}
                      onChange={(e) => {
                        setLessorFormData({ ...lessorFormData, identityNumber: e.target.value });
                        if (lessorFormErrors.identityNumber) {
                          setLessorFormErrors({ ...lessorFormErrors, identityNumber: '' });
                        }
                      }}
                      placeholder={t('brokerageAgreement.create.modals.addLessor.placeholders.enterIdentityNumber')}
                      required
                    />
                  </div>
                  {lessorFormSubmitted && !lessorFormData.identityNumber.trim() && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.identityNumberRequired')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-phone"></i> {t('brokerageAgreement.create.modals.addLessor.fields.mobileCountryCode')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-phone input-icon"></i>
                    <select
                      className={`form-select ${lessorFormSubmitted && !lessorFormData.mobileCountryCode ? 'is-invalid' : ''}`}
                      value={lessorFormData.mobileCountryCode}
                      onChange={(e) => {
                        setLessorFormData({ ...lessorFormData, mobileCountryCode: e.target.value });
                        if (lessorFormErrors.mobileCountryCode) {
                          setLessorFormErrors({ ...lessorFormErrors, mobileCountryCode: '' });
                        }
                      }}
                      required
                    >
                      <option value="">-- {t('contract.create.fields.select') || t('common.select') || 'Select'} --</option>
                      {(dropdowns.countries || []).map(country => (
                        <option key={country.id || country.Id} value={country.phoneCode || country.PhoneCode}>
                          {country.phoneCode || country.PhoneCode} - {country.nameEng || country.NameEng}
                        </option>
                      ))}
                    </select>
                  </div>
                  {lessorFormSubmitted && !lessorFormData.mobileCountryCode && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.mobileCountryCodeRequired')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-mobile-alt"></i> {t('brokerageAgreement.create.modals.addLessor.fields.mobileNumber')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-mobile-alt input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${lessorFormSubmitted && !lessorFormData.mobileNumber.trim() ? 'is-invalid' : ''}`}
                      value={lessorFormData.mobileNumber}
                      onChange={(e) => {
                        setLessorFormData({ ...lessorFormData, mobileNumber: e.target.value });
                        if (lessorFormErrors.mobileNumber) {
                          setLessorFormErrors({ ...lessorFormErrors, mobileNumber: '' });
                        }
                      }}
                      placeholder={t('brokerageAgreement.create.modals.addLessor.placeholders.enterMobileNumber')}
                      required
                    />
                  </div>
                  {lessorFormSubmitted && !lessorFormData.mobileNumber.trim() && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.mobileNumberRequired')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-envelope"></i> {t('brokerageAgreement.create.modals.addLessor.fields.email')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-envelope input-icon"></i>
                    <input
                      type="email"
                      className={`form-input ${lessorFormSubmitted && (!lessorFormData.emailId.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lessorFormData.emailId)) ? 'is-invalid' : ''}`}
                      value={lessorFormData.emailId}
                      onChange={(e) => {
                        setLessorFormData({ ...lessorFormData, emailId: e.target.value });
                        if (lessorFormErrors.emailId) {
                          setLessorFormErrors({ ...lessorFormErrors, emailId: '' });
                        }
                      }}
                      placeholder={t('brokerageAgreement.create.modals.addLessor.placeholders.enterEmailAddress')}
                      required
                    />
                  </div>
                  {lessorFormSubmitted && !lessorFormData.emailId.trim() && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.emailRequired')}
                    </div>
                  )}
                  {lessorFormSubmitted && lessorFormData.emailId.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lessorFormData.emailId) && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.emailInvalid')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-university"></i> {t('brokerageAgreement.create.modals.addLessor.fields.bank')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-university input-icon"></i>
                    <select
                      className={`form-select ${lessorFormSubmitted && !lessorFormData.bankId ? 'is-invalid' : ''}`}
                      value={lessorFormData.bankId}
                      onChange={(e) => {
                        setLessorFormData({ ...lessorFormData, bankId: e.target.value });
                        if (lessorFormErrors.bankId) {
                          setLessorFormErrors({ ...lessorFormErrors, bankId: '' });
                        }
                      }}
                      required
                    >
                      <option value="">-- {t('contract.create.fields.select') || t('common.select') || 'Select'} --</option>
                      {(dropdowns.banks || []).map(bank => (
                        <option key={bank.id || bank.Id} value={bank.id || bank.Id}>
                          {bank.nameEng || bank.NameEng || bank.bankNameEng || bank.BankNameEng || ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  {lessorFormSubmitted && !lessorFormData.bankId && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.bankRequired')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-wallet"></i> {t('brokerageAgreement.create.modals.addLessor.fields.bankAccountNumber')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-wallet input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${lessorFormSubmitted && !lessorFormData.bankAccountNumber.trim() ? 'is-invalid' : ''}`}
                      value={lessorFormData.bankAccountNumber}
                      onChange={(e) => {
                        setLessorFormData({ ...lessorFormData, bankAccountNumber: e.target.value });
                        if (lessorFormErrors.bankAccountNumber) {
                          setLessorFormErrors({ ...lessorFormErrors, bankAccountNumber: '' });
                        }
                      }}
                      placeholder={t('brokerageAgreement.create.modals.addLessor.placeholders.enterBankAccountNumber')}
                      required
                    />
                  </div>
                  {lessorFormSubmitted && !lessorFormData.bankAccountNumber.trim() && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.bankAccountNumberRequired')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-map"></i> {t('brokerageAgreement.create.modals.addLessor.fields.area')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-map input-icon"></i>
                    <select
                      className={`form-select ${lessorFormSubmitted && !lessorFormData.areaId ? 'is-invalid' : ''}`}
                      value={lessorFormData.areaId}
                      onChange={(e) => {
                        handleLessorAreaChange(e.target.value);
                        if (lessorFormErrors.areaId) {
                          setLessorFormErrors({ ...lessorFormErrors, areaId: '' });
                        }
                      }}
                      required
                    >
                      <option value="">-- {t('contract.create.fields.select') || t('common.select') || 'Select'} --</option>
                      {(dropdowns.areas || []).map(area => (
                        <option key={area.id || area.Id} value={area.id || area.Id}>
                          {area.areaNameEng || area.AreaNameEng}
                        </option>
                      ))}
                    </select>
                  </div>
                  {lessorFormSubmitted && !lessorFormData.areaId && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.areaRequired')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-city"></i> {t('brokerageAgreement.create.modals.addLessor.fields.city')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-city input-icon"></i>
                    <select
                      className={`form-select ${lessorFormSubmitted && !lessorFormData.cityId ? 'is-invalid' : ''}`}
                      value={lessorFormData.cityId}
                      onChange={(e) => {
                        handleLessorCityChange(e.target.value);
                        if (lessorFormErrors.cityId) {
                          setLessorFormErrors({ ...lessorFormErrors, cityId: '' });
                        }
                      }}
                      disabled={!lessorFormData.areaId}
                      required
                    >
                      <option value="">-- {lessorFormData.areaId ? (t('contract.create.fields.select') || t('common.select') || 'Select') : (t('contract.create.fields.selectAreaFirst') || 'Select Area First')} --</option>
                      {getFilteredCities(lessorFormData.areaId).map(city => (
                        <option key={city.id || city.Id} value={city.id || city.Id}>
                          {city.cityNameEng || city.CityNameEng}
                        </option>
                      ))}
                    </select>
                  </div>
                  {lessorFormSubmitted && !lessorFormData.cityId && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.cityRequired')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-map-pin"></i> {t('brokerageAgreement.create.modals.addLessor.fields.district')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-map-pin input-icon"></i>
                    <select
                      className={`form-select ${lessorFormSubmitted && !lessorFormData.districtId ? 'is-invalid' : ''}`}
                      value={lessorFormData.districtId}
                      onChange={(e) => {
                        setLessorFormData({ ...lessorFormData, districtId: e.target.value });
                        if (lessorFormErrors.districtId) {
                          setLessorFormErrors({ ...lessorFormErrors, districtId: '' });
                        }
                      }}
                      disabled={!lessorFormData.cityId}
                      required
                    >
                      <option value="">-- {lessorFormData.cityId ? (t('contract.create.fields.select') || t('common.select') || 'Select') : (t('contract.create.fields.selectCityFirst') || 'Select City First')} --</option>
                      {getFilteredDistricts(lessorFormData.cityId).map(district => (
                        <option key={district.id || district.Id} value={district.id || district.Id}>
                          {district.districtNameEng || district.DistrictNameEng}
                        </option>
                      ))}
                    </select>
                  </div>
                  {lessorFormSubmitted && !lessorFormData.districtId && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.districtRequired')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-road"></i> {t('brokerageAgreement.create.modals.addLessor.fields.streetName')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-road input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      value={lessorFormData.streetName}
                      onChange={(e) => setLessorFormData({ ...lessorFormData, streetName: e.target.value })}
                      placeholder={t('brokerageAgreement.create.modals.addLessor.placeholders.enterStreetName')}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-building"></i> {t('brokerageAgreement.create.modals.addLessor.fields.buildingNumber')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-building input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      value={lessorFormData.buildingNumber}
                      onChange={(e) => setLessorFormData({ ...lessorFormData, buildingNumber: e.target.value })}
                      placeholder={t('brokerageAgreement.create.modals.addLessor.placeholders.enterBuildingNumber')}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-plus"></i> {t('brokerageAgreement.create.modals.addLessor.fields.additionalNumber')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-plus input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      value={lessorFormData.additionalNumber}
                      onChange={(e) => setLessorFormData({ ...lessorFormData, additionalNumber: e.target.value })}
                      placeholder={t('brokerageAgreement.create.modals.addLessor.placeholders.enterAdditionalNumber')}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-mail-bulk"></i> {t('brokerageAgreement.create.modals.addLessor.fields.postalCode')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-mail-bulk input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      value={lessorFormData.postalCode}
                      onChange={(e) => setLessorFormData({ ...lessorFormData, postalCode: e.target.value })}
                      placeholder={t('brokerageAgreement.create.modals.addLessor.placeholders.enterPostalCode')}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary modal-btn-equal-width" onClick={() => {
                setShowAddLessorModal(false);
                setLessorFormSubmitted(false);
                setLessorFormErrors({});
                setEditingOwnershipId(null);
              }}>
                {t('brokerageAgreement.create.buttons.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary modal-btn-equal-width"
                onClick={async () => {
                  setLessorFormSubmitted(true);
                  
                  if (!propertyId) {
                    setErrorMessage(t('brokerageAgreement.create.messages.propertyRequired'));
                    setShowErrorModal(true);
                    return;
                  }

                  const errors = {};
                  if (!lessorFormData.nameAr.trim()) {
                    errors.nameAr = t('contract.create.errors.nameArRequired');
                  }
                  if (!lessorFormData.nameEng.trim()) {
                    errors.nameEng = t('contract.create.errors.nameEngRequired');
                  }
                  if (!lessorFormData.dob) {
                    errors.dob = t('contract.create.errors.dobRequired');
                  }
                  if (!lessorFormData.nationalityId) {
                    errors.nationalityId = t('contract.create.errors.nationalityRequired');
                  }
                  if (!lessorFormData.identityTypeId) {
                    errors.identityTypeId = t('contract.create.errors.identityTypeRequired');
                  }
                  if (!lessorFormData.identityNumber.trim()) {
                    errors.identityNumber = t('contract.create.errors.identityNumberRequired');
                  }
                  if (!lessorFormData.mobileCountryCode) {
                    errors.mobileCountryCode = t('contract.create.errors.mobileCountryCodeRequired');
                  }
                  if (!lessorFormData.mobileNumber.trim()) {
                    errors.mobileNumber = t('contract.create.errors.mobileNumberRequired');
                  }
                  if (!lessorFormData.emailId.trim()) {
                    errors.emailId = t('contract.create.errors.emailRequired');
                  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lessorFormData.emailId)) {
                    errors.emailId = t('contract.create.errors.emailInvalid');
                  }
                  if (!lessorFormData.bankId) {
                    errors.bankId = t('contract.create.errors.bankRequired');
                  }
                  if (!lessorFormData.bankAccountNumber.trim()) {
                    errors.bankAccountNumber = t('contract.create.errors.bankAccountNumberRequired');
                  }
                  if (!lessorFormData.areaId) {
                    errors.areaId = t('contract.create.errors.areaRequired');
                  }
                  if (!lessorFormData.cityId) {
                    errors.cityId = t('contract.create.errors.cityRequired');
                  }
                  if (!lessorFormData.districtId) {
                    errors.districtId = t('contract.create.errors.districtRequired');
                  }

                  if (Object.keys(errors).length > 0) {
                    setLessorFormErrors(errors);
                    return;
                  }

                  setLessorFormErrors({});

                  try {
                    const ownershipData = {
                      propertyId: propertyId,
                      ownershipNameAr: lessorFormData.nameAr,
                      ownershipNameEng: lessorFormData.nameEng,
                      dob: lessorFormData.dob || null,
                      nationalityId: lessorFormData.nationalityId ? parseInt(lessorFormData.nationalityId) : null,
                      identityTypeId: lessorFormData.identityTypeId ? parseInt(lessorFormData.identityTypeId) : null,
                      identityNumber: lessorFormData.identityNumber || null,
                      mobileNumberCountryCode: lessorFormData.mobileCountryCode || null,
                      mobileNumber: lessorFormData.mobileNumber || null,
                      emailId: lessorFormData.emailId || null,
                      bankId: lessorFormData.bankId ? parseInt(lessorFormData.bankId) : null,
                      bankAccountNumber: lessorFormData.bankAccountNumber || null,
                      areaId: lessorFormData.areaId ? parseInt(lessorFormData.areaId) : null,
                      cityId: lessorFormData.cityId ? parseInt(lessorFormData.cityId) : null,
                      districtId: lessorFormData.districtId ? parseInt(lessorFormData.districtId) : null,
                      streetName: lessorFormData.streetName || null,
                      buildingNumber: lessorFormData.buildingNumber || null,
                      additionalNumber: lessorFormData.additionalNumber || null,
                      postalCode: lessorFormData.postalCode || null,
                      isAgent: false,
                      agentNumber: null,
                      ownerId: null
                    };

                    if (editingOwnershipId) {
                      // Update existing ownership
                      ownershipData.id = editingOwnershipId;
                      // Update in local state
                      const updatedOwnerships = propertyOwnerships.map(o => {
                        const oId = o.id || o.Id;
                        if (oId === editingOwnershipId) {
                          return { ...o, ...ownershipData };
                        }
                        return o;
                      });
                      setPropertyOwnerships(updatedOwnerships);
                      // Save to backend
                      await savePropertyOwnerships();
                      setSuccessMessage(t('contract.create.messages.lessorUpdatedSuccessfully'));
                    } else {
                      // Create new ownership
                      const response = await contractAPI.createPropertyOwnership(ownershipData);
                      const createdOwnership = response.data;

                      // Reload ownerships list
                    await loadPropertyOwnerships(propertyId);

                      // Auto-select the newly created ownership
                    setLessor({ propertyOwnershipId: createdOwnership.id || createdOwnership.Id });
                      setSuccessMessage(t('contract.create.messages.lessorCreatedSuccessfully'));
                    }

                    setShowSuccessModal(true);
                    setShowAddLessorModal(false);
                    setEditingOwnershipId(null);
                    setLessorFormSubmitted(false);
                    setLessorFormErrors({});

                    // Reset form
                    setLessorFormData({
                      agentNumber: '',
                      nameAr: '',
                      nameEng: '',
                      dob: '',
                      nationalityId: '',
                      identityTypeId: '',
                      identityNumber: '',
                      mobileCountryCode: '',
                      mobileNumber: '',
                      emailId: '',
                      bankId: '',
                      bankAccountNumber: '',
                      areaId: '',
                      cityId: '',
                      districtId: '',
                      streetName: '',
                      buildingNumber: '',
                      additionalNumber: '',
                      postalCode: ''
                    });
                  } catch (err) {
                    console.error('Failed to save lessor:', err);
                    setErrorMessage(err.response?.data?.message || (editingOwnershipId ? t('contract.create.messages.failedToUpdateLessor') : t('contract.create.messages.failedToCreateLessor')));
                    setShowErrorModal(true);
                  }
                }}
              >
                <i className="fas fa-save"></i> {t('brokerageAgreement.create.buttons.save')}
              </button>
            </div>
          </div>
        </div>
      )}


      <SuccessModal
        show={showSuccessModal}
        message={successMessage}
        onClose={() => setShowSuccessModal(false)}
      />

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />

      <ConfirmModal
        show={showConfirmModal}
        title={confirmData?.title || t('common.confirmDelete')}
        message={confirmData?.message || t('common.confirmDeleteMessage')}
        confirmText={confirmData?.confirmText || t('common.delete')}
        cancelText={confirmData?.cancelText || t('common.cancel')}
        onConfirm={() => {
          if (confirmAction) {
            confirmAction();
          }
        }}
        onCancel={() => {
          setShowConfirmModal(false);
          setConfirmAction(null);
          setConfirmData(null);
        }}
      />
    </div>
  );
};

export default BrokerageAgreementCreate;
